import { Button, Text } from '@/components';
import type { BatchModel } from '@/features/batch/_models/batch';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { InvestmentFundReportFormat } from '@/features/mold/components/pdf/investment-fund/InvestmentFund';
import { LocalGovernmentFundTableAFormat } from '@/features/mold/components/pdf/local-government-fund/TableA';
import { LocalGovernmentFundTableBFormat } from '@/features/mold/components/pdf/local-government-fund/TableB';
import { OrganizingTableFormat1 } from '@/features/mold/components/pdf/organizing-table/Format1';
import { OrganizingTableFormat2 } from '@/features/mold/components/pdf/organizing-table/Format2';
import { OrganizingTableFormat3 } from '@/features/mold/components/pdf/organizing-table/Format3';
import { OrganizingTableFormat4 } from '@/features/mold/components/pdf/organizing-table/Format4';
import { OrganizingTableFormat5 } from '@/features/mold/components/pdf/organizing-table/Format5';
import { StandardFundReportForPrivateFormat } from '@/features/mold/components/pdf/standard-fund/Private';
import { StandardFundReportForPublicFormat } from '@/features/mold/components/pdf/standard-fund/Public';
import { reportRsBatchTargetType } from '@/features/mold/models';
import { generateDownloadPdfData } from '@/features/mold/utils/pdf/generateDownloadPdfData';
import type {
  InvestmentFundTableProps,
  LocalGovernmentFundTableProps,
  OrganizingTableFormat1Props,
  OrganizingTableFormat2Props,
  OrganizingTableFormat3Props,
  OrganizingTableFormat4Props,
  OrganizingTableFormat5Props,
  StandardFundReportProps,
} from '@/features/mold/utils/pdf/pdf-components-props';
import classNames from 'classnames';
import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import type { z } from 'zod';
import styles from './PdfPrint.module.scss';

const printHiddenClass = classNames(styles.print_only);

type Props = {
  batch: z.infer<typeof BatchModel>;
  isLoading: boolean;
  jsonData: any;
};

export const DownloadPdf: FC<Props> = ({ batch, jsonData, isLoading }) => {
  const { t } = useTranslation();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;
  const contentRef = useRef<HTMLDivElement>(null);

  const target = reportRsBatchTargetType.safeParse(batch.target);

  const pdfData = generateDownloadPdfData({
    jsonData,
    target: target.success ? target.data : 'rs-report1',
    dateCreated: batch.taskResult.dateCreated,
    batchParams: batch.params,
    isDebugMode,
  });

  const handlePrint = useReactToPrint({
    pageStyle: `@page { size: ${pdfData.result !== 'error' ? pdfData.data.paperSize : 'A4'} landscape; margin: 10mm 5mm; }`,
    contentRef,
    documentTitle: pdfData.result !== 'error' ? pdfData.fileName : '',
  });

  if (!target.success || isLoading) {
    return null;
  }

  return pdfData.result === 'error' ? (
    <Text
      color="error"
      weight="regular"
      text={t('error-message-csv-export-empty-data')}
    />
  ) : (
    <>
      <Button type="primary" onClick={handlePrint}>
        {t('export-file')}
      </Button>
      <div
        ref={contentRef}
        className={isDebugMode ? undefined : printHiddenClass}
      >
        {(() => {
          const type = pdfData.result;
          switch (type) {
            case 'rs-report1': {
              const data = pdfData.data as OrganizingTableFormat1Props;
              return <OrganizingTableFormat1 {...data} />;
            }
            case 'rs-report2': {
              const data = pdfData.data as OrganizingTableFormat2Props;
              return <OrganizingTableFormat2 {...data} />;
            }
            case 'rs-report3': {
              const data = pdfData.data as OrganizingTableFormat3Props;
              return <OrganizingTableFormat3 {...data} />;
            }
            case 'rs-report4': {
              const data = pdfData.data as OrganizingTableFormat4Props;
              return <OrganizingTableFormat4 {...data} />;
            }
            case 'rs-report5': {
              const data = pdfData.data as OrganizingTableFormat5Props;
              return <OrganizingTableFormat5 {...data} />;
            }
            case 'ks-standard-list': {
              const data = pdfData.data as StandardFundReportProps;
              if (data.reportType === 'public') {
                return <StandardFundReportForPublicFormat {...data} />;
              }
              return <StandardFundReportForPrivateFormat {...data} />;
            }
            case 'ks-local-government-list-A': {
              const data = pdfData.data as LocalGovernmentFundTableProps;
              return <LocalGovernmentFundTableAFormat {...data} />;
            }
            case 'ks-local-government-list-B': {
              const data = pdfData.data as LocalGovernmentFundTableProps;
              return <LocalGovernmentFundTableBFormat {...data} />;
            }
            case 'ks-investment-list': {
              const data = pdfData.data as InvestmentFundTableProps;
              return <InvestmentFundReportFormat {...data} />;
            }
            default: {
              const check: never = type;
              throw new Error(`Unexpected target: ${check}`);
            }
          }
        })()}
      </div>
    </>
  );
};
